import { IconSearch } from '@maia/icons';
import { Input, Pagination, Table, ToggleButtonGroup } from '@maia/ui';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useEffect } from 'react';
import { useFilter } from '../../../utils/filter/filter';
import { Quotations, getQuotationsOptions } from './queryOptions';
import { useQuotationsTable } from './tableOptions';
import { IQuotationEntity } from '@maia/shared';

interface Props {
  quotations: Quotations;
}

export function QuotationsTable(props: Props) {
  const { quotations } = props;
  const { state, dispatch: setSearch, mappedState } = useFilter('/quotations');

  const { data } = useQuery(getQuotationsOptions(mappedState, quotations));

  const table = useQuotationsTable(data);

  const { sorting } = table.getState();

  useEffect(() => {
    setSearch({ type: 'setSort', payload: sorting });
  }, [setSearch, sorting]);

  const debouncedQuery = debounce(
    (payload: string) => setSearch({ type: 'setQuery', payload }),
    500,
  );

  return (
    <Table
      table={table}
      className="grid-cols-[min-content_auto_auto_auto_min-content_min-content] "
      filterSlot={
        <div className="flex gap-x-24">
          <Input
            icon={<IconSearch />}
            placeholder="Zoeken"
            className="w-full max-w-[550px] mr-auto"
            onChange={(event) => debouncedQuery(event.target.value)}
            defaultValue={state.search}
          />
          <ToggleButtonGroup
            defaultSelectedKey={
              state.filter?.find(([key]) => key === 'status')?.[1]
            }
            items={[
              {
                label: 'Alle',
                value: '',
              },
              {
                label: 'Concept',
                value: 'concept' satisfies IQuotationEntity['status'],
              },
              {
                label: 'Ready',
                value: 'ready' satisfies IQuotationEntity['status'],
              },
              {
                label: 'In afwachting',
                value: 'pending' satisfies IQuotationEntity['status'],
              },
              {
                label: 'Geaccepteerd',
                value: 'accepted' satisfies IQuotationEntity['status'],
              },
              {
                label: 'Afgewezen',
                value: 'rejected' satisfies IQuotationEntity['status'],
              },
            ]}
            onChange={(status) =>
              setSearch({ type: 'setFilter', payload: ['status', status] })
            }
          />
        </div>
      }
      paginationSlot={
        data?.pagination ? (
          <Pagination
            {...data.pagination}
            onLimitChange={(limit) =>
              setSearch({ type: 'setLimit', payload: limit })
            }
            onPageChange={(page) =>
              setSearch({ type: 'paginate', payload: page })
            }
          />
        ) : null
      }
    />
  );
}
